import { LOCALIZATION_SCHEMA_VERSION } from "./localization-contract.js"

/**
 * Collapse whitespace for stable identity (does not trim intentional inner meaning of templates).
 * @param {string} template
 * @returns {string}
 */
export function normalizeTemplate(template) {
    if (template == null || typeof template !== "string") return ""
    return template.replace(/\s+/g, " ").trim()
}

/**
 * Deterministic hash (djb2 XOR variant), hex string.
 * @param {string} str
 * @returns {string}
 */
export function djb2Hex(str) {
    let hash = 5381
    for (let i = 0; i < str.length; i++) {
        hash = (hash * 33) ^ str.charCodeAt(i)
    }
    return (hash >>> 0).toString(16)
}

/**
 * Stable message id for a template + schema version.
 * @param {string} template
 * @param {string} [explicitId]
 * @param {number} [schemaVersion]
 * @returns {string}
 */
export function deriveMessageId(template, explicitId, schemaVersion = LOCALIZATION_SCHEMA_VERSION) {
    if (explicitId && String(explicitId).trim()) return String(explicitId).trim()
    const norm = normalizeTemplate(template)
    return `m_${schemaVersion}_${djb2Hex(norm)}`
}

/**
 * Extract placeholder names in order (including repeats), respecting `{{` / `}}` escapes.
 * @param {string} template
 * @returns {string[]}
 */
export function extractPlaceholderNames(template) {
    if (!template) return []
    const names = []
    let i = 0
    while (i < template.length) {
        if (template[i] === "{" && template[i + 1] === "{") {
            i += 2
            continue
        }
        if (template[i] === "}" && template[i + 1] === "}") {
            i += 2
            continue
        }
        if (template[i] === "{") {
            const end = template.indexOf("}", i + 1)
            if (end === -1) break
            const raw = template.slice(i + 1, end).trim()
            if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(raw)) names.push(raw)
            i = end + 1
            continue
        }
        i++
    }
    return names
}

/**
 * Compare two placeholder name lists for multiset equality.
 * @param {string[]} a
 * @param {string[]} b
 * @returns {boolean}
 */
export function placeholderListsEqual(a, b) {
    if (a.length !== b.length) return false
    const sa = [...a].sort()
    const sb = [...b].sort()
    for (let i = 0; i < sa.length; i++) {
        if (sa[i] !== sb[i]) return false
    }
    return true
}
